import React, { useState, useEffect } from 'react';
import logger from '../../utils/logger';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  Card,
  Grid,
  Fade,
  Zoom
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { KeyboardArrowDown } from '@mui/icons-material';
import {
  TrendingUp,
  TrendingDown,
  Star,
  RateReview,
  Analytics,
  Refresh,
  Info
} from '@mui/icons-material';
import DashboardLayout from '../Layouts/DashboardLayout';
import PageHeader from '../Dashboard/components/PageHeader';
import BenchmarkingTableModern from './BenchmarkingTableModern';
import ReviewsOverviewModern from './ReviewsOverviewModern';
import StatisticsSectionModern from './StatisticsSectionModern';
import MonthlyAnalysisModern from './MonthlyAnalysisModern';
import DynamicRatingChart from './DynamicRatingChart';
import InteractivePerformanceWidget from './InteractivePerformanceWidget';
import GMBService from '../../services/GMBService';
import tokenManager from '../../auth/TokenManager';
import cacheManager from '../../utils/cacheManager';
import './ReputationResponsiveFixes.css';

const PageContainer = styled(Box)(({ theme }) => ({
  maxWidth: '1280px',
  margin: '0 auto',
  padding: '12px',
  paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
  backgroundColor: '#f8fafc',
  minHeight: '100vh',
  overflow: 'hidden',
  width: '100%',
  boxSizing: 'border-box',
  [theme.breakpoints.up('md')]: {
    padding: '32px 50px'
  },
  [theme.breakpoints.up('lg')]: {
    padding: '50px 50px'
  }
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
  borderRadius: '12px',
  marginBottom: '16px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '28px 32px',
    gap: '24px',
    borderRadius: '16px',
    marginBottom: '24px'
  }
}));

const HeaderLeft = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  [theme.breakpoints.up('md')]: {
    gap: '16px'
  }
}));

const HeaderIconBox = styled(Box)(({ theme }) => ({
  width: '40px',
  height: '40px',
  borderRadius: '10px',
  background: 'rgba(255, 255, 255, 0.15)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backdropFilter: 'blur(10px)',
  flexShrink: 0,
  [theme.breakpoints.up('md')]: {
    width: '52px',
    height: '52px',
    borderRadius: '14px'
  }
}));

const HeaderInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '2px'
});

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '18px',
  fontWeight: 700,
  color: 'white',
  lineHeight: 1.2,
  [theme.breakpoints.up('md')]: {
    fontSize: '22px'
  }
}));

const HeaderSubtitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 400,
  color: 'rgba(255, 255, 255, 0.7)',
  lineHeight: 1.4,
  display: 'none',
  [theme.breakpoints.up('md')]: {
    display: 'block',
    fontSize: '14px'
  }
}));

const HeaderControls = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: '12px',
    width: 'auto'
  }
}));

const ProfileSelector = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  background: 'rgba(255, 255, 255, 0.95)',
  borderRadius: '10px',
  padding: '8px 12px',
  width: '100%',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  minHeight: '44px',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    background: 'white',
    transform: 'scale(0.99)'
  },
  [theme.breakpoints.up('md')]: {
    minWidth: '280px',
    width: 'auto',
    padding: '8px 12px 8px 16px',
    '&:hover': {
      background: 'white',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
    }
  }
}));

const ProfileIcon = styled(Box)(({ theme }) => ({
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: 'white',
  fontSize: '12px',
  fontWeight: 600,
  flexShrink: 0,
  [theme.breakpoints.up('md')]: {
    width: '36px',
    height: '36px',
    borderRadius: '10px',
    fontSize: '14px'
  }
}));

const ProfileInfo = styled(Box)({
  flex: 1,
  minWidth: 0
});

const ProfileName = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  color: '#1e293b',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  [theme.breakpoints.up('md')]: {
    fontSize: '14px'
  }
}));

const ProfileAddress = styled(Typography)({
  fontFamily: 'Inter, sans-serif',
  fontSize: '11px',
  color: '#64748b',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis'
});

const RefreshButton = styled(Button)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  minHeight: '44px',
  color: 'white',
  textTransform: 'none',
  padding: '0 16px',
  borderRadius: '10px',
  background: 'rgba(255, 255, 255, 0.15)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.2s ease',
  width: '100%',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    background: 'rgba(255, 255, 255, 0.25)'
  },
  '&:disabled': {
    background: 'rgba(255, 255, 255, 0.08)',
    color: 'rgba(255, 255, 255, 0.4)'
  },
  [theme.breakpoints.up('md')]: {
    width: 'auto',
    padding: '0 20px',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.25)'
    }
  }
}));

const BenchmarkingSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  width: '100%',
  overflow: 'hidden',
  [theme.breakpoints.up('md')]: {
    gap: '24px'
  }
}));

const MetricsGrid = styled(Grid)(({ theme }) => ({
  marginBottom: '16px',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    marginBottom: '32px'
  }
}));

const MetricCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  border: '1px solid rgba(11, 145, 214, 0.1)',
  transition: 'all 0.2s ease',
  overflow: 'hidden',
  position: 'relative',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    transform: 'scale(0.99)'
  },
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #0B91D6, #34A853, #EF232A)',
    opacity: 0,
    transition: 'opacity 0.3s ease'
  },
  [theme.breakpoints.up('md')]: {
    borderRadius: '16px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
      borderColor: 'rgba(11, 145, 214, 0.2)'
    },
    '&:hover::before': {
      opacity: 1
    }
  }
}));

const BenchmarkingContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  width: '100%',
  overflow: 'hidden',
  [theme.breakpoints.up('md')]: {
    gap: '32px'
  }
}));

const ChartSection = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  padding: '16px',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  border: '1px solid rgba(11, 145, 214, 0.1)',
  marginBottom: '16px',
  width: '100%',
  overflow: 'hidden',
  boxSizing: 'border-box',
  [theme.breakpoints.up('md')]: {
    borderRadius: '20px',
    padding: '32px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.06)',
    marginBottom: '32px'
  }
}));

const ChartContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '24px',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'row',
    gap: '48px'
  }
}));

const ChartLegend = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: '8px',
  justifyContent: 'center',
  [theme.breakpoints.up('md')]: {
    flexDirection: 'column',
    gap: '16px'
  }
}));

const LegendItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '6px 10px',
  borderRadius: '6px',
  transition: 'all 0.2s ease',
  [theme.breakpoints.up('md')]: {
    gap: '12px',
    padding: '8px 12px',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: 'rgba(11, 145, 214, 0.05)',
      transform: 'translateX(4px)'
    }
  }
}));

const LegendDot = styled(Box)(({ theme, color }) => ({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: color,
  boxShadow: `0 2px 6px ${color}40`,
  flexShrink: 0,
  [theme.breakpoints.up('md')]: {
    width: '12px',
    height: '12px'
  }
}));

const LegendText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  color: '#121927',
  [theme.breakpoints.up('md')]: {
    fontSize: '14px'
  }
}));

const SectionCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  padding: '0',
  background: 'white',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  border: '1px solid rgba(11, 145, 214, 0.1)',
  marginBottom: '16px',
  overflow: 'hidden',
  width: '100%',
  boxSizing: 'border-box',
  [theme.breakpoints.up('md')]: {
    borderRadius: '20px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
    marginBottom: '32px'
  }
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  padding: '16px',
  background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
  borderBottom: '1px solid rgba(11, 145, 214, 0.1)',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
  [theme.breakpoints.up('md')]: {
    padding: '24px 32px',
    gap: '12px'
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontFamily: 'Inter, sans-serif',
  fontSize: '16px',
  fontWeight: 600,
  color: '#121927',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  [theme.breakpoints.up('md')]: {
    fontSize: '20px'
  }
}));

const LoadingCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  padding: '48px 24px',
  textAlign: 'center',
  background: 'white',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  border: '1px solid rgba(11, 145, 214, 0.1)',
  [theme.breakpoints.up('md')]: {
    borderRadius: '20px',
    padding: '64px 32px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
  }
}));

const ErrorCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  padding: '24px',
  background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
  border: '1px solid rgba(239, 35, 42, 0.2)',
  boxShadow: '0 2px 8px rgba(239, 35, 42, 0.06)',
  [theme.breakpoints.up('md')]: {
    borderRadius: '20px',
    padding: '32px',
    boxShadow: '0 4px 20px rgba(239, 35, 42, 0.08)'
  }
}));

const ReputationBenchmarking = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [gmbData, setGmbData] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [reputationData, setReputationData] = useState(null);
  const [availableProfiles, setAvailableProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState('');

  const legendItems = [
    { color: '#0B91D6', label: 'Average Rating' },
    { color: '#34A853', label: 'Response Rate' },
    { color: '#EF232A', label: 'Negative Reviews' }
  ];

  // Load available GMB profiles (same as BusinessProfile page)
  useEffect(() => {
    const loadAvailableProfiles = async () => {
      try {
        setLoading(true);
        setError('');

        // Get accounts directly from Google API (same as BusinessProfile page)
        const accounts = await GMBService.getAccounts();

        if (accounts.length === 0) {
          setError('No GMB accounts found. Please connect your Google Business Profile first.');
          setLoading(false);
          return;
        }

        // Get all locations for all accounts
        const allLocations = [];
        for (const acct of accounts) {
          try {
            if (!acct?.name) continue;
            const locs = await GMBService.getLocations(undefined, acct.name);
            if (Array.isArray(locs) && locs.length) {
              locs.forEach(l => allLocations.push({ account: acct, loc: l }));
            }
          } catch (e) {
            logger.warn('Failed to fetch locations for account', acct?.name, e?.message || e);
          }
        }

        // Format profiles for selector
        const profiles = allLocations.map(({ account, loc }) => ({
          id: loc.name?.split('/').pop(),
          name: loc.title || loc.locationName || 'Unnamed Location',
          address: loc.storefrontAddress?.addressLines?.join(', ') || loc.storefrontAddress?.locality || 'No address',
          accountName: account.accountName || 'Unnamed Account',
          locationData: loc,
          accountData: account
        }));

        setAvailableProfiles(profiles);

        // Auto-select stored profile or first available
        const storedGMBData = localStorage.getItem('selectedGMBLocation');
        if (storedGMBData) {
          const locationData = JSON.parse(storedGMBData);
          const locationId = locationData.name?.split('/').pop();
          const matchingProfile = profiles.find(p => p.id === locationId);
          if (matchingProfile) {
            setSelectedProfileId(matchingProfile.id);
            loadReputationDataForProfile(matchingProfile);
            return;
          }
        }

        // Select first profile if available
        if (profiles.length > 0) {
          setSelectedProfileId(profiles[0].id);
          loadReputationDataForProfile(profiles[0]);
        } else {
          setError('No GMB profiles found.');
          setLoading(false);
        }

      } catch (err) {
        logger.error('Error loading profiles:', err);
        setError('Failed to load GMB profiles: ' + (err.message || 'Unknown error'));
        setLoading(false);
      }
    };

    loadAvailableProfiles();
  }, []);

  const loadReputationDataForProfile = async (profile) => {
    try {
      setLoading(true);
      setError('');
      setSelectedLocation(profile.locationData);

      // Store selected profile
      localStorage.setItem('selectedGMBLocation', JSON.stringify(profile.locationData));
      localStorage.setItem('selectedGMBAccount', JSON.stringify(profile.accountData));

      // Try to get from cache first
      const cacheKey = `reputation_${profile.locationData.name}`;
      const { data: cachedMetrics, fromCache } = await cacheManager.getOrFetch(
        cacheKey,
        async () => {
          logger.log('🔄 Fetching reputation data from API...');

          // Try to fetch fresh reviews (same as BusinessProfile page)
          try {
            const reviews = await GMBService.getReviews(undefined, profile.accountData.name, profile.locationData.name);

            // Fetch competitor data based on location
            const competitorData = await fetchCompetitorData(profile.locationData);

            const reputationMetrics = calculateReputationMetrics(reviews, profile.locationData, competitorData);
            return reputationMetrics;

          } catch (apiError) {
            logger.warn('Could not fetch fresh reviews, using location metadata:', apiError);
            const basicMetrics = calculateBasicMetrics(profile.locationData);
            return basicMetrics;
          }
        },
        20 * 60 * 1000 // 20 minutes cache for reputation data
      );

      if (fromCache) {
        logger.log('✅ Loaded reputation data from cache - instant!');
        // Set loading to false immediately when using cache
        setLoading(false);
      } else {
        logger.log('🔄 Fetched reputation data from API - cached for next time');
      }

      setReputationData(cachedMetrics);

    } catch (err) {
      logger.error('Error loading reputation data:', err);
      setError('Failed to load reputation data');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetitorData = async (locationData) => {
    try {
      // Extract location information for competitor analysis
      const placeId = locationData.metadata?.placeId;
      const address = locationData.storefrontAddress;
      const categories = locationData.categories || [];
      const latlng = locationData.latlng;

      logger.log('Fetching competitor data from DataForSEO for location:', {
        placeId,
        address: address?.locality,
        categories: categories.map(c => c.displayName),
        coordinates: latlng
      });

      // Get coordinates from location data
      const latitude = latlng?.latitude;
      const longitude = latlng?.longitude;

      if (!latitude || !longitude) {
        logger.warn('No coordinates available for competitor search');
        return null;
      }

      // Get the primary business category as keyword
      const keyword = categories[0]?.displayName || categories[0]?.name || 'Business';

      // Import and use CompetitorDiscoveryService to get real data from DataForSEO
      const CompetitorDiscoveryService = (await import('../../services/CompetitorDiscoveryService')).default;

      const competitors = await CompetitorDiscoveryService.findCompetitors({
        latitude,
        longitude,
        keyword,
        radius: 10000, // 10km radius
        excludePlaceId: placeId
      });

      if (!competitors || competitors.length === 0) {
        logger.warn('No competitors found from DataForSEO');
        return null;
      }

      logger.log(`Found ${competitors.length} competitors from DataForSEO`);

      // Calculate city average from competitor data
      const validRatings = competitors.filter(c => c.rating > 0);
      const validReviews = competitors.filter(c => c.reviewsCount > 0);

      const avgRating = validRatings.length > 0
        ? validRatings.reduce((sum, c) => sum + c.rating, 0) / validRatings.length
        : 4.0;

      const avgReviewCount = validReviews.length > 0
        ? validReviews.reduce((sum, c) => sum + c.reviewsCount, 0) / validReviews.length
        : 100;

      // Find the top competitor (highest rating with decent review count)
      const topCompetitor = competitors
        .filter(c => c.rating >= 4.0 && c.reviewsCount >= 10)
        .sort((a, b) => {
          // Sort by rating first, then by review count
          if (b.rating !== a.rating) return b.rating - a.rating;
          return b.reviewsCount - a.reviewsCount;
        })[0] || competitors[0];

      return {
        cityAverage: {
          rating: Number(avgRating.toFixed(1)),
          reviewCount: Math.round(avgReviewCount),
          responseRate: 35, // Estimated - not available from DataForSEO
          negativeRatio: 15 // Estimated - not available from DataForSEO
        },
        topCompetitor: {
          name: topCompetitor?.name || 'Top Competitor',
          rating: topCompetitor?.rating || 4.5,
          reviewCount: topCompetitor?.reviewsCount || 200,
          responseRate: 50, // Estimated
          negativeRatio: 10 // Estimated
        },
        nearbyCompetitors: competitors.slice(0, 10).map(c => ({
          name: c.name,
          rating: c.rating || 0,
          reviewCount: c.reviewsCount || 0,
          address: c.address,
          distance: c.distance,
          responseRate: 30, // Estimated
          negativeRatio: 18 // Estimated
        })),
        totalCompetitorsFound: competitors.length,
        dataSource: 'DataForSEO'
      };
    } catch (error) {
      logger.error('Error fetching competitor data from DataForSEO:', error);
      return null;
    }
  };

  const handleProfileChange = (event) => {
    const profileId = event.target.value;
    setSelectedProfileId(profileId);

    const profile = availableProfiles.find(p => p.id === profileId);
    if (profile) {
      loadReputationDataForProfile(profile);
    }
  };

  const calculateReputationMetrics = (reviews, locationData, competitorData = null) => {
    // Handle both old format (array) and new format (object with reviews array)
    const reviewsArray = Array.isArray(reviews) ? reviews : (reviews?.reviews || []);
    const reviewsSampleSize = reviewsArray.length;

    // Get REAL data from GMB API response - this is the authoritative source
    // The actual rating shown on Google Maps (from GMB reviews API)
    const actualProfileRating = reviews?.averageRating ||
      locationData?.averageRating ||
      locationData?.profile?.averageRating ||
      locationData?.metadata?.averageRating ||
      null;

    // The actual total review count from GMB API (from GMB reviews API)
    const actualTotalReviews = reviews?.totalReviewCount ||
      reviews?.total ||
      locationData?.profile?.reviewCount ||
      locationData?.metadata?.reviewCount ||
      locationData?.reviewCount ||
      locationData?.profile?.totalReviewCount ||
      locationData?.totalReviewCount ||
      reviewsSampleSize;

    logger.log('GMB Profile Data:', {
      actualProfileRating,
      actualTotalReviews,
      reviewsSampleSize,
      locationDataKeys: Object.keys(locationData || {})
    });

    // Helper to convert star rating
    const toNumericRating = (star) => {
      if (typeof star === 'number') return star;
      const map = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };
      const key = String(star || '').toUpperCase();
      return map[key] || 0;
    };

    // Use actual GMB profile rating if available, otherwise calculate from sample
    const numericRatings = reviewsArray.map(r => toNumericRating(r.starRating));
    const calculatedAvgRating = reviewsSampleSize > 0 ?
      numericRatings.reduce((sum, n) => sum + n, 0) / reviewsSampleSize : 0;

    // Prefer actual profile rating from GMB over calculated
    const avgRating = actualProfileRating || calculatedAvgRating || 4.0;

    logger.log('Rating - Actual GMB:', actualProfileRating, 'Calculated from sample:', calculatedAvgRating, 'Using:', avgRating);

    // Calculate negative ratio from available reviews sample
    const negativeReviews = numericRatings.filter(rating => rating <= 2).length;
    const negativeRatio = reviewsSampleSize > 0 ? (negativeReviews / reviewsSampleSize) * 100 : 0;

    // Calculate response rate from available reviews sample
    const reviewsWithResponses = reviewsArray.filter(review => review.reviewReply && review.reviewReply.comment).length;
    const responseRate = reviewsSampleSize > 0 ? (reviewsWithResponses / reviewsSampleSize) * 100 : 0;

    // Use real competitor data if available, otherwise generate realistic data
    let cityAverage, topCompetitor;

    if (competitorData) {
      cityAverage = competitorData.cityAverage;
      topCompetitor = competitorData.topCompetitor;
      logger.log('Using real competitor data:', competitorData);
    } else {
      // Fallback to generated data
      cityAverage = {
        rating: Math.max(1, Number((avgRating * 0.9).toFixed(1))),
        responseRate: Math.max(0, Math.round(responseRate - 15)),
        negativeRatio: Math.min(100, Math.round(negativeRatio + 10)),
        reviewCount: Math.max(0, Math.floor(actualTotalReviews * 8.5))
      };

      topCompetitor = {
        rating: Math.max(1, Number((avgRating * 0.95).toFixed(1))),
        responseRate: Math.max(0, Math.round(responseRate - 5)),
        negativeRatio: Math.min(100, Math.round(negativeRatio + 5)),
        reviewCount: Math.max(0, Math.floor(actualTotalReviews * 3))
      };
    }

    logger.log('Final reputation metrics:', {
      avgRating,
      actualTotalReviews,
      reviewsSampleSize,
      responseRate,
      negativeRatio,
      competitorDataUsed: !!competitorData
    });

    return {
      yourBusiness: {
        name: locationData.title || locationData.locationName || 'Your Business',
        rating: Number(avgRating.toFixed(1)),
        responseRate: Math.round(responseRate),
        negativeRatio: Math.round(negativeRatio),
        reviewCount: actualTotalReviews
      },
      cityAverage,
      topCompetitor,
      reviews: reviewsArray,
      competitorData: competitorData
    };
  };

  const calculateBasicMetrics = (locationData) => {
    // When no reviews are available, use fallback values
    // This should rarely be used since we prioritize fetching reviews
    logger.log('Using basic metrics fallback for location:', locationData.title);

    const safeRating = 4.2; // Default fallback
    const safeReviewCount = 0; // No reviews available

    logger.log('Basic metrics fallback - Rating:', safeRating, 'Reviews:', safeReviewCount);

    return {
      yourBusiness: {
        name: locationData.title || locationData.locationName || 'Your Business',
        rating: Number(safeRating.toFixed(1)),
        responseRate: 45,
        negativeRatio: 8,
        reviewCount: safeReviewCount
      },
      cityAverage: {
        rating: Number(Math.max(1, safeRating * 0.9).toFixed(1)),
        responseRate: 30,
        negativeRatio: 18,
        reviewCount: Math.max(0, Math.floor(safeReviewCount * 8.5))
      },
      topCompetitor: {
        rating: Number(Math.max(1, safeRating * 0.95).toFixed(1)),
        responseRate: 40,
        negativeRatio: 12,
        reviewCount: Math.max(0, Math.floor(safeReviewCount * 3))
      },
      reviews: []
    };
  };

  const handleSyncContact = async () => {
    if (selectedProfileId) {
      const profile = availableProfiles.find(p => p.id === selectedProfileId);
      if (profile) {
        loadReputationDataForProfile(profile);
      }
    }
  };

  const selectedProfile = availableProfiles.find(p => p.id === selectedProfileId);
  const getProfileInitials = (name) => {
    if (!name) return 'BP';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <DashboardLayout>
      <PageContainer>
        <HeaderSection>
          <HeaderLeft>
            <HeaderIconBox>
              <Analytics sx={{ color: 'white', fontSize: '24px' }} />
            </HeaderIconBox>
            <HeaderInfo>
              <HeaderTitle>Reputation Benchmarking</HeaderTitle>
              <HeaderSubtitle>Track performance against industry standards</HeaderSubtitle>
            </HeaderInfo>
          </HeaderLeft>

          <HeaderControls>
            <FormControl size="small" sx={{ minWidth: 280 }} data-tutorial="reputation-profile">
              <Select
                value={selectedProfileId}
                onChange={handleProfileChange}
                disabled={loading || availableProfiles.length === 0}
                displayEmpty
                IconComponent={KeyboardArrowDown}
                sx={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  borderRadius: '12px',
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '&:hover': { background: 'white' },
                  '& .MuiSelect-select': { py: '10px', px: '16px' }
                }}
                renderValue={() => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <ProfileIcon>{getProfileInitials(selectedProfile?.name)}</ProfileIcon>
                    <ProfileInfo>
                      <ProfileName>{selectedProfile?.name || 'Select Profile'}</ProfileName>
                      <ProfileAddress>{selectedProfile?.address || 'No profile selected'}</ProfileAddress>
                    </ProfileInfo>
                  </Box>
                )}
              >
                {availableProfiles.map((profile) => (
                  <MenuItem key={profile.id} value={profile.id} sx={{ py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <Box sx={{
                        width: 32, height: 32, borderRadius: '8px',
                        background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: 'white', fontSize: '12px', fontWeight: 600
                      }}>
                        {getProfileInitials(profile.name)}
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1e293b' }}>
                          {profile.name}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#64748b' }}>
                          {profile.address}
                        </Typography>
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <RefreshButton
              onClick={handleSyncContact}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Refresh sx={{ fontSize: '18px' }} />}
            >
              {loading ? 'Syncing...' : 'Refresh'}
            </RefreshButton>
          </HeaderControls>
        </HeaderSection>

        <BenchmarkingSection>
          {loading ? (
            <Fade in timeout={300}>
              <LoadingCard>
                <CircularProgress size={48} sx={{ color: '#0B91D6', mb: 2 }} />
                <Typography sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '16px',
                  fontWeight: 500,
                  color: '#121927'
                }}>
                  Loading reputation data...
                </Typography>
                <Typography sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#666',
                  mt: 1
                }}>
                  Analyzing your business performance
                </Typography>
              </LoadingCard>
            </Fade>
          ) : error ? (
            <Zoom in timeout={400}>
              <ErrorCard>
                <Alert
                  severity="error"
                  sx={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    '& .MuiAlert-message': {
                      fontFamily: 'Inter, sans-serif',
                      fontSize: '14px'
                    }
                  }}
                >
                  {error}
                </Alert>
              </ErrorCard>
            </Zoom>
          ) : (
            <BenchmarkingContent>
              <Fade in timeout={800}>
                <ChartSection>
                  <SectionTitle sx={{ mb: 3, fontSize: '18px' }}>
                    <Star sx={{ color: '#0B91D6' }} />
                    Performance Overview
                  </SectionTitle>
                  <InteractivePerformanceWidget reputationData={reputationData} />
                </ChartSection>
              </Fade>

              <Fade in timeout={1000}>
                <SectionCard>
                  <SectionHeader>
                    <SectionTitle>
                      <RateReview sx={{ color: '#0B91D6' }} />
                      Competitive Analysis
                    </SectionTitle>
                  </SectionHeader>
                  <Box sx={{ padding: '24px 32px' }}>
                    <BenchmarkingTableModern reputationData={reputationData} />
                  </Box>
                </SectionCard>
              </Fade>
            </BenchmarkingContent>
          )}
        </BenchmarkingSection>

        {!loading && !error && reputationData && (
          <>
            <Fade in timeout={1200}>
              <SectionCard data-tutorial="reviews-overview">
                <SectionHeader>
                  <SectionTitle>
                    <Star sx={{ color: '#0B91D6' }} />
                    Reviews Overview
                  </SectionTitle>
                </SectionHeader>
                <Box sx={{ padding: '0' }}>
                  <ReviewsOverviewModern reputationData={reputationData} />
                </Box>
              </SectionCard>
            </Fade>

            <Fade in timeout={1400}>
              <Box>
                <StatisticsSectionModern reputationData={reputationData} />
              </Box>
            </Fade>

            <Fade in timeout={1600}>
              <Box>
                <MonthlyAnalysisModern reputationData={reputationData} />
              </Box>
            </Fade>
          </>
        )}
      </PageContainer>
    </DashboardLayout>
  );
};

export default ReputationBenchmarking;